import { DEFAULT_NEXT } from "./redirectUser";

export type TNextURIPolicy = (nextURL: URL, location: Pick<Location, "origin">) => boolean | Promise<boolean>;

interface IResolveNextURIOptions {
  fallbackURI?: string;
  location?: Pick<Location, "origin">;
  policy?: TNextURIPolicy;
}

/**
 * The default frontend policy is deliberately stricter than the backend
 * allowlist: without consulting the backend, only this application's origin
 * can be trusted.
 *
 * A backend policy can later replace this function, or be composed with it,
 * without changing any redirect call sites.
 */
export const sameOriginNextURIPolicy: TNextURIPolicy = (nextURL, location) => nextURL.origin === location.origin;

export const combineNextURIPolicies = (...policies: TNextURIPolicy[]): TNextURIPolicy => {
  return async (nextURL, location) => {
    const results = await Promise.all(policies.map((policy) => policy(nextURL, location)));
    return results.every(Boolean);
  };
};

/**
 * Resolve an untrusted nextURI to a safe navigation target.
 *
 * Scheme and URL-shape checks are always enforced here. The policy is the
 * replaceable authorization boundary: today it is same-origin; later it can
 * call a backend endpoint which owns the hostname allowlist.
 */
export const resolveNextURI = async (
  nextURI: string,
  {
    fallbackURI = DEFAULT_NEXT,
    location = window.location,
    policy = sameOriginNextURIPolicy,
  }: IResolveNextURIOptions = {},
): Promise<string> => {
  try {
    if (!nextURI || nextURI !== nextURI.trim() || nextURI.startsWith("//") || nextURI.includes("\\")) {
      return fallbackURI;
    }

    const nextURL = new URL(nextURI, location.origin);
    if (!["http:", "https:"].includes(nextURL.protocol) || nextURL.username || nextURL.password) {
      return fallbackURI;
    }

    if (!(await policy(nextURL, location))) {
      return fallbackURI;
    }

    return nextURL.origin === location.origin
      ? `${nextURL.pathname}${nextURL.search}${nextURL.hash}`
      : nextURL.toString();
  } catch {
    return fallbackURI;
  }
};

export const redirectToValidatedNextURI = async (nextURI: string): Promise<void> => {
  window.location.replace(await resolveNextURI(nextURI));
};
